import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useRouterState } from '@tanstack/react-router';
import { toast } from 'react-toastify';
import {
  LiliToastDefaultConfig,
  LiliNotification,
  LiliNotificationType,
} from '@lili/lili-frontend-common';
import { HttpService } from '@Services/index';
import { useBoundStore } from '@Store/index';
import { useCustomerName } from '@Pages/passwordless/welcome/hooks/useCustomerName';
import { routesModel } from '@Router/router.consts';
import { RoutesMap } from '@Router/router.types.ts';
import { useNavigationManager } from '@Router/useNavigationManager';

const useGetSuggestions = () => {
  const customerId = useBoundStore((state) => state.customer.customerId);
  const { activeUboFirstName, activeUboLastName, getIsEmbedded } = useBoundStore(
    (state) => state.onboardingSlice
  );
  const { firstName, lastName } = useBoundStore((state) => state.customer.customerData);
  return useMutation({
    mutationKey: ['name-too-long-suggestions'],
    mutationFn: async () => {
      const url = getIsEmbedded()
        ? `v3/secureonboarding/partner/user/${customerId}/card/displayNameSuggestions`
        : `v2/secureonboarding/user/${customerId}/card/displayNameSuggestions`;

      const res = await HttpService.post(url, {
        firstName: activeUboFirstName || firstName,
        lastName: activeUboLastName || lastName,
      });

      return res.data.displayNames;
    },
  });
};

export const useNameTooLongLogic = () => {
  const { activeUboFirstName, activeUboLastName, setGlobalCardName } = useBoundStore(
    (state) => state.onboardingSlice
  );
  const { firstName, lastName } = useBoundStore((state) => state.customer.customerData);
  const { mutateAsync, isPending, data, reset } = useGetSuggestions();
  const [selectedName, setSelectedName] = useState('');
  const { mutateAsync: updateCustomerName, isPending: isUpdatingName } = useCustomerName();
  const PATH_TO_AVOID_NAME_UPDATE = [routesModel[RoutesMap.ADD_OWNER].path as string];
  const { navigateToNextPage } = useNavigationManager();
  const {
    location: { pathname },
  } = useRouterState();

  const nameUpdateHappyPath = async () => {
    const nameParts = selectedName?.trim().split(' ') || [];
    const firstPart = nameParts[0] || '';
    const restOfParts = nameParts?.slice(1).join(' ') || '';

    await updateCustomerName({
      firstName: firstName || '',
      lastName: lastName || '',
      cardFirstName: firstPart,
      cardLastName: restOfParts,
    });
  };

  const closeModal = () => {
    setSelectedName('');
    useBoundStore.getState().errorSlice.setIsNameTooLongModalShown(false);
  };

  const triggerSubmitOnMainPage = () => {
    const elBtn = document.getElementById('formSubmitButton');
    if (elBtn) {
      elBtn.click();
    }
  };

  const triggerConfirmationToast = () => {
    toast(
      <LiliNotification
        type={LiliNotificationType.SUCCESS}
        text='Credit card name updated successfully'
        id='name-suggestion-confirmation-toast'
      />,
      { ...LiliToastDefaultConfig, closeOnClick: true }
    );
  };

  const handleContinue = async () => {
    if (pathname === routesModel[RoutesMap.REVIEW].path) {
      await nameUpdateHappyPath();
      const nameParts = selectedName?.trim().split(' ') || [];
      setGlobalCardName(nameParts[0] || '', nameParts[1] || '');
      closeModal();
      triggerSubmitOnMainPage();
    } else if (PATH_TO_AVOID_NAME_UPDATE.includes(pathname)) {
      const nameParts = selectedName?.trim().split(' ') || [];
      setGlobalCardName(nameParts[0] || '', nameParts[1] || '');
      closeModal();
      triggerSubmitOnMainPage();
      triggerConfirmationToast();
    } else {
      await nameUpdateHappyPath();
      closeModal();
      navigateToNextPage('SKIP');
    }
  };

  return {
    fetchSuggestions: mutateAsync,
    isFetchingSuggestions: isPending,
    suggestions: data,
    selectedName,
    setSelectedName,
    reset,
    firstName: activeUboFirstName || firstName,
    lastName: activeUboLastName || lastName,
    handleContinue,
    isUpdatingName,
    closeModal,
  };
};
